import { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useAuthStore } from "../stores/useAuthStore"
import { useRouteId } from "../lib/routeHelper"
import { apiClient } from "../lib/api"
import { isRestaurantOpen } from "../lib/date"
import type { ClientUnpaidOrdersResponse, Offer, TenantConfig, ClientAcceptedOrdersResponse } from "../types"

export const useDashboard = () => {
    const routeId = useRouteId()
    const navigate = useNavigate()
    const location = useLocation()
    const { isAuthenticated, isTakeAway, tableId: storeTableId, shouldPollOrders, orderTiming, isSessionClosed } = useAuthStore()

    const [unpaidOrders, setUnpaidOrders] = useState<ClientUnpaidOrdersResponse | null>(null)
    const [offers, setOffers] = useState<Offer[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [showOrderSuccess, setShowOrderSuccess] = useState(false)
    const [waiterCalled, setWaiterCalled] = useState(false)
    const [showWaiterModal, setShowWaiterModal] = useState(false)
    const [showCancelWaiterModal, setShowCancelWaiterModal] = useState(false)
    const [showChatbot, setShowChatbot] = useState(false)
    const [config, setConfig] = useState<TenantConfig | null>(null)
    const [isOpen, setIsOpen] = useState(true)
    const [isAccordionOpen, setIsAccordionOpen] = useState(false)
    const [showScheduledSkeleton, setShowScheduledSkeleton] = useState(false)

    const [showErrorModal, setShowErrorModal] = useState(false)
    const [errorModalMessage, setErrorModalMessage] = useState<string>("")

    const [showPendingCollectionOrderModal, setShowPendingCollectionOrderModal] = useState(false)
    const [showPendingCollectionPaymentModal, setShowPendingCollectionPaymentModal] = useState(false)

    useEffect(() => {
        if (!isAuthenticated) {
            navigate(`/loading/${routeId}`)
            return
        }

        if (location.state?.orderCreated) {
            setShowOrderSuccess(true)
            if (orderTiming === 'week') {
                setShowScheduledSkeleton(true)
            }
            window.history.replaceState({}, document.title)
            setTimeout(() => setShowOrderSuccess(false), 3000)
        }

        loadData()
    }, [isAuthenticated, routeId, navigate, location.state, isTakeAway, storeTableId, orderTiming])

    useEffect(() => {
        let interval: NodeJS.Timeout

        if (shouldPollOrders && isAuthenticated) {
            interval = setInterval(() => {
                loadUnpaidOrders()
            }, 60000)
        }

        return () => {
            if (interval) clearInterval(interval)
        }
    }, [shouldPollOrders, isAuthenticated])

    useEffect(() => {
        if (isAuthenticated && isTakeAway && !orderTiming) {
            navigate(`/order-timing/${routeId || storeTableId || 'default'}`, { replace: true })
        }
    }, [isAuthenticated, isTakeAway, orderTiming, navigate, routeId, storeTableId])

    const loadData = async () => {
        if (!storeTableId && !isTakeAway) {
            setError("No se pudo identificar la mesa. Por favor escanea el QR nuevamente.")
            setLoading(false)
            return
        }

        try {
            setLoading(true)
            setError(null)

            const [configData, offersData] = await Promise.all([
                apiClient.getConfig(),
                apiClient.getOffers()
            ])

            const tenantConfig = (configData as any).config as TenantConfig
            setConfig(tenantConfig)
            setIsOpen(isRestaurantOpen(tenantConfig))
            setOffers(offersData as Offer[])

            if (storeTableId || isTakeAway) {
                await loadUnpaidOrders()
            }
        } catch (error) {
            console.error("Error loading data:", error)
            setError("Error al cargar los datos")
        } finally {
            setLoading(false)
        }
    }

    const loadUnpaidOrders = async () => {
        try {
            let unpaidOrdersData: ClientUnpaidOrdersResponse | null = null;

            if (isTakeAway) {
                const response = await apiClient.getClientAcceptedOrders();
                // Map accepted orders to unpaid orders structure
                // Set total_amount_owed to 0 as they are already paid
                unpaidOrdersData = {
                    session_id: response.session_id,
                    table_uuid: response.table_uuid,
                    table_number: response.table_number,
                    orders: response.orders,
                    total_amount_owed: 0,
                    unpaid_orders_count: response.accepted_orders_count
                };
            } else {
                unpaidOrdersData = await apiClient.getClientUnpaidOrders();
            }

            setUnpaidOrders(unpaidOrdersData)
            if (unpaidOrdersData && unpaidOrdersData.orders.length > 0) {
                setShowScheduledSkeleton(false)
            }
        } catch (error) {
            console.error("Error loading unpaid orders:", error)
        }
    }

    const handleCallWaiter = () => {
        setShowWaiterModal(true)
    }

    const confirmCallWaiter = async () => {
        if (!storeTableId) return

        try {
            const response = await apiClient.callWaiter(storeTableId)
            if (response.calling) {
                setWaiterCalled(true)
                setShowWaiterModal(false)
            }
        } catch (error) {
            console.error("Error calling waiter:", error)
            setErrorModalMessage("Error al llamar al mozo. Intenta nuevamente.")
            setShowErrorModal(true)
            setShowWaiterModal(false)
        }
    }

    const handleCancelWaiter = async () => {
        if (!storeTableId) return

        try {
            await apiClient.cancelWaiterCall()
            setWaiterCalled(false)
            setShowCancelWaiterModal(false)
        } catch (error) {
            console.error("Error canceling waiter call:", error)
            setErrorModalMessage("Error al cancelar llamado. Intenta nuevamente.")
            setShowErrorModal(true)
        }
    }

    const handleOpenChatbot = () => {
        setShowChatbot(true)
    }

    const handleOrderClick = () => {
        const hasPendingCollection = unpaidOrders?.orders.some(o => o.pending_collection)

        if (hasPendingCollection) {
            setShowPendingCollectionOrderModal(true)
        } else {
            if (!isOpen) {
                if (orderTiming !== 'week') {
                    if (config?.accept_scheduled_orders) {
                        setErrorModalMessage("El restaurante se encuentra cerrado para pedidos inmediatos. Podés realizar un pedido programado.")
                    } else {
                        setErrorModalMessage("El restaurante se encuentra cerrado en este momento y no acepta pedidos programados.")
                    }
                    setShowErrorModal(true)
                    return
                } else if (!config?.accept_scheduled_orders) {
                    setErrorModalMessage("En este momento no se aceptan pedidos programados.")
                    setShowErrorModal(true)
                    return
                }
            }

            if (orderTiming === 'week') {
                navigate(`/scheduled-orders/${routeId}`)
            } else {
                navigate(`/menu/${routeId}`)
            }
        }
    }

    const handlePaymentClick = () => {
        const hasPendingCollection = unpaidOrders?.orders.some(o => o.pending_collection)

        if (hasPendingCollection) {
            setShowPendingCollectionPaymentModal(true)
        } else {
            if (isTakeAway) {
                navigate(`/payment/${routeId}`, {
                    state: {
                        paymentType: "individual",
                        unpaidOrders,
                    },
                })
                return
            }

            apiClient
                .getOpenSessions()
                .then((response) => {
                    if (response.open_sessions > 1) {
                        navigate(`/payment-split/${routeId}`, {
                            state: { unpaidOrders },
                        })
                    } else {
                        navigate(`/payment/${routeId}`, {
                            state: {
                                paymentType: "individual",
                                unpaidOrders,
                            },
                        })
                    }
                })
                .catch((error) => {
                    console.error("Error checking open sessions:", error)
                    navigate(`/payment-split/${routeId}`, {
                        state: { unpaidOrders },
                    })
                })
        }
    }

    const confirmPendingCollectionOrder = () => {
        setShowPendingCollectionOrderModal(false)
        navigate(`/menu/${routeId}`)
    }

    return {
        routeId,
        isAuthenticated,
        isTakeAway,
        isSessionClosed,
        orderTiming,
        unpaidOrders,
        offers,
        loading,
        error,
        showOrderSuccess,
        waiterCalled,
        showWaiterModal,
        setShowWaiterModal,
        showCancelWaiterModal,
        setShowCancelWaiterModal,
        showChatbot,
        setShowChatbot,
        config,
        isOpen,
        isAccordionOpen,
        setIsAccordionOpen,
        showScheduledSkeleton,
        showErrorModal,
        setShowErrorModal,
        errorModalMessage,
        showPendingCollectionOrderModal,
        setShowPendingCollectionOrderModal,
        showPendingCollectionPaymentModal,
        setShowPendingCollectionPaymentModal,
        loadData,
        loadUnpaidOrders,
        handleCallWaiter,
        confirmCallWaiter,
        handleCancelWaiter,
        handleOpenChatbot,
        handleOrderClick,
        handlePaymentClick,
        confirmPendingCollectionOrder
    }
}
